import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import Swal from "sweetalert2";
import CommonFilterSelect, {
  FilterTypes,
  initialFilters,
} from "../../../core/common/CommonFilterSelect";
import { OrderFilterProps } from "./AssignOrderToCandidate";
import { DatePicker } from "antd";
import dayjs from "dayjs";

interface CandidateForm {
  first_name: string;
  middle_name: string;
  last_name: string;
  applicant_email: string;
  applicant_mobile: string;
  dob: string;
  gender: string;
  parent_name: string;
  uan_no: string;
  aadharNumber: string;
  aadhar_pincode: string;
  address_pincode: string;
  pan_no: string;
  address1: string;
  address2: string;
  crn_no: string;
  hrmsId: string;
  employeeId: string;
  joiningDate: string;
  spocName: string;
}

const initialState: CandidateForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  applicant_email: "",
  applicant_mobile: "",
  dob: "",
  gender: "",
  parent_name: "",
  uan_no: "",
  aadharNumber: "",
  aadhar_pincode: "",
  address_pincode: "",
  pan_no: "",
  address1: "",
  address2: "",
  crn_no: "",
  hrmsId: "",
  employeeId: "",
  joiningDate: "",
  spocName: "",
};

const getDateValue = (value: any): string => {
  if (!value) return "";

  const date = String(value).slice(0, 10);
  const match = date.match(/^(\d{2})-(\d{2})-(\d{4})$/);

  return match ? `${match[3]}-${match[2]}-${match[1]}` : date;
};

const getSelectedValue = (value: any): string => {
  if (value == null) return "";

  if (typeof value === "object") {
    return String(
      value.value ??
        value.id ??
        value.org_id ??
        value.project_id ??
        value.program_id ??
        value.department_id ??
        value.package_id ??
        "",
    );
  }

  return String(value);
};

const ReviewCandidate = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const candidate = location.state?.candidate;
  const orderId = location.state?.orderId;
  console.log("candidate", candidate);

  const [formData, setFormData] = useState<CandidateForm>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { callApi, loading: loadingCreate } = useApi<any>(
    webservice_url.assignOrderToSingleCase,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getOrderDetails = useApi<any>(
    orderId ? `${webservice_url.getCheckOrderHierarchy}/${orderId}` : "",
    {
      method: "GET",
      autoCall: !!orderId,
      handleGlobalError: false,
    },
  );

  useEffect(() => {
    if (!orderId) return;

    const data = (() => {
      const res: any = getOrderDetails.data;
      const responseData = res?.data ?? res;
      return Array.isArray(responseData) ? responseData[0] : responseData;
    })();

    if (!data || typeof data !== "object") return;

    setOrderFilter({
      organization: data.org_name ?? data.name ?? "",
      org_id: String(data.org_id ?? ""),
      project: data.project_name ?? "",
      project_id: String(data.project_id ?? ""),
      location: data.location_name ?? data.program_name ?? "",
      program: data.program_name ?? "",
      program_id: String(data.program_id ?? ""),
      department: data.department_name ?? "",
      department_id: String(data.department_id ?? ""),
      package: data.package_name ?? "",
      package_id: String(data.package_id ?? ""),
      package_description: data.package_description ?? "",
    });
  }, [orderId, getOrderDetails.data]);

  const [orderFilter, setOrderFilter] = useState<OrderFilterProps>({
    organization: "",
    org_id: "",
    project: "",
    project_id: "",
    location: "",
    program: "",
    program_id: "",
    department: "",
    department_id: "",
    package: "",
    package_id: "",
    package_description: "",
  });

  const [filterData, setFilterData] = useState<FilterTypes>(initialFilters);
  const hasOrderConfiguration = Boolean(
    orderFilter?.program_id?.trim() &&
    orderFilter?.department_id?.trim() &&
    orderFilter?.package_id?.trim(),
  );

  useEffect(() => {
    if (!candidate) return;

    const address = candidate.metadata?.address ?? {};

    setFormData({
      first_name: candidate.first_name ?? candidate.firstName ?? "",
      middle_name: candidate.middle_name ?? candidate.middleName ?? "",
      last_name: candidate.last_name ?? candidate.lastName ?? "",
      applicant_email:
        candidate.applicant_email ??
        candidate.applicantEmailId ??
        candidate.email ??
        "",
      applicant_mobile: String(
        candidate.applicant_mobile ??
          candidate.applicantMobileNumber ??
          candidate.phone ??
          "",
      ),
      dob: getDateValue(candidate.dob ?? candidate.dateOfBirth),
      gender: candidate.gender ?? "",
      parent_name:
        candidate.parent_name ??
        candidate.father_or_mother_name ??
        candidate.fatherNameMotherName ??
        candidate.father_name ??
        "",
      uan_no: String(candidate.uan_no ?? ""),
      aadharNumber: String(
        candidate.aadharNumber ?? candidate.aadhar_number ?? "",
      ),
      aadhar_pincode: String(candidate.aadhar_pincode ?? ""),
      address_pincode: String(
        candidate.address_pincode ?? address.pinCode ?? "",
      ),
      pan_no: candidate.pan_no ?? candidate.panNumber ?? "",
      address1: candidate.address1 ?? address.addressLine1 ?? "",
      address2: candidate.address2 ?? address.addressLine2 ?? "",
      crn_no: candidate.crn_no ?? candidate.crn ?? "",
      hrmsId: String(candidate.hrmsId ?? candidate.hrms_id ?? ""),
      employeeId: String(candidate.employeeId ?? candidate.employee_id ?? ""),
      joiningDate: getDateValue(
        candidate.joiningDate ?? candidate.joining_date,
      ),
      spocName: candidate.spocName ?? candidate.spoc_name ?? "",
    });

    setErrors({});
  }, [candidate]);

  // Original configuration IDs from the candidate response.
  const orgId = String(
    candidate?.org_id ??
      candidate?.organization_id ??
      candidate?.organization?.org_id ??
      "",
  );

  const projectId = String(
    candidate?.project_id ??
      candidate?.projectId ??
      candidate?.project?.project_id ??
      "",
  );

  const packageId = String(
    candidate?.package_id ?? candidate?.package?.package_id ?? "",
  );

  const programId = String(
    candidate?.program_id ?? candidate?.program?.program_id ?? "",
  );

  const departmentId = String(
    candidate?.department_id ?? candidate?.department?.department_id ?? "",
  );

  const selectedOrgId = getSelectedValue(
    filterData?.organization ?? (filterData as any)?.org_id,
  );
  const selectedProjectId = getSelectedValue(filterData?.project);
  const selectedPackageId = getSelectedValue(filterData?.package);
  const selectedProgramId = getSelectedValue(filterData?.program);
  const selectedDepartmentId = getSelectedValue(filterData?.department);

  // Use original IDs if all exist; otherwise use filter selections.
  const finalOrgId = hasOrderConfiguration ? orgId : selectedOrgId;
  const finalProjectId = hasOrderConfiguration ? projectId : selectedProjectId;
  const finalPackageId = hasOrderConfiguration ? packageId : selectedPackageId;
  const finalProgramId = hasOrderConfiguration ? programId : selectedProgramId;
  const finalDepartmentId = hasOrderConfiguration
    ? departmentId
    : selectedDepartmentId;

  const invalidReason =
    candidate?.metadata?.reason ?? "Invalid reason is not available.";

  const duplicateField = candidate?.metadata?.duplicate_field ?? "";
  const duplicateValue = candidate?.metadata?.duplicate_value ?? "";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      nextErrors.first_name = "First name is required.";
    }

    if (!formData.last_name.trim()) {
      nextErrors.last_name = "Last name is required.";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.applicant_email.trim())) {
      nextErrors.applicant_email = "Enter a valid email address.";
    }

    if (!/^[6-9]\d{9}$/.test(formData.applicant_mobile.trim())) {
      nextErrors.applicant_mobile = "Enter a valid 10-digit mobile number.";
    }

    if (!formData.dob) {
      nextErrors.dob = "Date of birth is required.";
    }

    if (!formData.gender) {
      nextErrors.gender = "Please select gender.";
    }

    if (!formData.parent_name.trim()) {
      nextErrors.parent_name = "Father name is required.";
    }

    if (!formData.pan_no.trim()) {
      nextErrors.pan_no = "PAN number is required.";
    } else if (
      !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan_no.trim().toUpperCase())
    ) {
      nextErrors.pan_no = "Enter a valid PAN number.";
    }

    if (!/^\d{12}$/.test(formData.aadharNumber.trim())) {
      nextErrors.aadharNumber = "Enter a valid 12-digit Aadhaar number.";
    }

    if (!formData.aadhar_pincode.trim()) {
      nextErrors.aadhar_pincode = "Aadhaar address PIN code is required.";
    } else if (!/^\d{6}$/.test(formData.aadhar_pincode.trim())) {
      nextErrors.aadhar_pincode =
        "Enter a valid 6-digit Aadhaar address PIN code.";
    }

    if (!formData.address1.trim()) {
      nextErrors.address1 = "Aadhaar address is required.";
    }

    if (!formData.hrmsId.trim()) {
      nextErrors.hrmsId = "Candidate ID is required.";
    }

    if (!formData.joiningDate) {
      nextErrors.joiningDate = "Hire date is required.";
    }

    if (!formData.spocName.trim()) {
      nextErrors.spocName = "SPOC name is required.";
    }

    if (
      !finalOrgId ||
      !finalProjectId ||
      !finalPackageId ||
      !finalProgramId ||
      !finalDepartmentId
    ) {
      nextErrors.configuration =
        "Please select Organization, Account, Program, Department and Package.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!orderId) {
      await Swal.fire({
        icon: "error",
        title: "Order Not Found",
        text: "Order ID is missing. Please return to the invalid candidates list and try again.",
        confirmButtonColor: "#d33",
      });
      return;
    }

    // if (!validateForm()) return;

    const payload = {
      come_from: "Invalid",
      org_id: finalOrgId,
      order_id: orderId,
      project_id: orderFilter?.project_id,
      package_id: orderFilter?.package_id,
      program_id: orderFilter?.program_id,
      department_id: orderFilter?.department_id,

      firstName: formData.first_name.trim(),
      middleName: formData.middle_name.trim(),
      lastName: formData.last_name.trim(),
      applicantEmailId: formData.applicant_email.trim(),
      applicantMobileNumber: formData.applicant_mobile.trim(),
      dateOfBirth: formData.dob,
      gender: formData.gender,
      fatherNameMotherName: formData.parent_name.trim(),

      uan_no: formData.uan_no.trim(),
      aadharNumber: formData.aadharNumber.trim(),
      aadhar_pincode: formData.aadhar_pincode.trim(),
      address_pincode: formData.address_pincode.trim(),
      pan_no: formData.pan_no.trim().toUpperCase(),

      address1: formData.address1.trim(),
      address2: formData.address2.trim(),
      crn_no: formData.crn_no.trim(),
      hrmsId: formData.hrmsId.trim(),
      employeeId: formData.employeeId.trim(),
      joiningDate: formData.joiningDate,
      spocName: formData.spocName.trim(),
    };

    try {
      const res = await callApi(
        payload,
        `${webservice_url.assignOrderToSingleCase}/${orderId}`,
      );

      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: res?.message || "Invite created successfully!",
          confirmButtonText: "OK",
          confirmButtonColor: "#f26522",
        });

        navigate(-1);
      } else if (res) {
        await Swal.fire({
          icon: "error",
          title: "Failed",
          text: res?.message || res?.error || "Invite failed.",
          confirmButtonText: "OK",
          confirmButtonColor: "#d33",
        });
      }
    } catch (error: any) {
      console.error("Candidate invite submission failed:", error);

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Invite failed. Please check the candidate details and try again.";

      await Swal.fire({
        icon: "error",
        title: "Failed",
        text: message,
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
      });
    }
  };

  // const renderField = (
  //   label: string,
  //   name: keyof CandidateForm,
  //   type = "text",
  //   required = false,
  //   maxLength?: number,
  // ) => (
  //   <div className="col-6 col-md-4 mb-3" key={name}>
  //     <label
  //       htmlFor={`review-${name}`}
  //       className="form-label fw-bold text-dark"
  //     >
  //       {label}
  //       {required && <span className="text-danger"> *</span>}
  //     </label>

  //     <input
  //       id={`review-${name}`}
  //       name={name}
  //       type={type}
  //       value={formData[name]}
  //       onChange={handleChange}
  //       disabled={loadingCreate}
  //       maxLength={maxLength}
  //       inputMode={
  //         [
  //           "applicant_mobile",
  //           "aadharNumber",
  //           "aadhar_pincode",
  //           "address_pincode",
  //         ].includes(name)
  //           ? "numeric"
  //           : undefined
  //       }
  //       className={`form-control ${errors[name] ? "is-invalid" : ""}`}
  //     />

  //     {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
  //   </div>
  // );

  const renderField = (
  label: string,
  name: keyof CandidateForm,
  type = "text",
  required = false,
  maxLength?: number,
) => (
  <div className="col-6 col-md-4 mb-3" key={name}>
    <label
      htmlFor={`review-${name}`}
      className="form-label fw-bold text-dark"
    >
      {label}
      {required && <span className="text-danger"> *</span>}
    </label>

    {type === "date" ? (
      <DatePicker
        id={`review-${name}`}
        className={`w-100 ${errors[name] ? "ant-picker-status-error" : ""}`}
        format="DD-MM-YYYY"
        placeholder="DD-MM-YYYY"
        value={formData[name] ? dayjs(formData[name], "YYYY-MM-DD") : null}
        onChange={(date) => {
          setFormData((prev) => ({
            ...prev,
            [name]: date ? date.format("YYYY-MM-DD") : "",
          }));

          setErrors((prev) => ({
            ...prev,
            [name]: "",
          }));
        }}
        disabled={loadingCreate}
        allowClear
        showToday
      />
    ) : (
      <input
        id={`review-${name}`}
        name={name}
        type={type}
        value={formData[name]}
        onChange={handleChange}
        disabled={loadingCreate}
        maxLength={maxLength}
        inputMode={
          [
            "applicant_mobile",
            "aadharNumber",
            "aadhar_pincode",
            "address_pincode",
          ].includes(name)
            ? "numeric"
            : undefined
        }
        className={`form-control ${errors[name] ? "is-invalid" : ""}`}
      />
    )}

    {errors[name] && (
      <div className="invalid-feedback d-block">{errors[name]}</div>
    )}
  </div>
);
  

  if (!candidate) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          Candidate details were not found. Please return to the invalid
          candidates list and select a candidate again.
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate(-1)}
        >
          Go Back
        </button>
      </div>
    );
  }

  const CaseMark = () => (
    <svg
      className="tcp-case-mark"
      viewBox="0 0 40 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="14" width="4" height="18" rx="1.5" />
      <rect x="9" y="8" width="4" height="24" rx="1.5" />
      <rect x="18" y="0" width="4" height="32" rx="1.5" />
      <rect x="27" y="10" width="4" height="22" rx="1.5" />
      <rect x="36" y="18" width="4" height="14" rx="1.5" />
    </svg>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="tcp-header-title-row">
            <CaseMark />
            <div>
              <h4 className="tcp-title">Review Candidate</h4>
              <p className="tcp-subtitle">
                Review and update candidate details, resolve validation issues,
                and validate the candidate before creating an invitation.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate(-1)}
            disabled={loadingCreate}
          >
            Back
          </button>
        </div>

        <div className="mb-4">
          {hasOrderConfiguration ? (
            <>
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden order-config-card">
                <div className="order-config-header d-flex align-items-center justify-content-between flex-wrap gap-2 px-4 py-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="order-config-icon">
                      <i className="ti ti-clipboard-check"></i>
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold text-dark">
                        Order Configuration
                      </h5>
                      <p className="mb-0 text-muted small">
                        Review the account and package details for this order.
                      </p>
                    </div>
                  </div>
                  <span className="order-config-badge">
                    <i className="ti ti-circle-check me-1"></i>
                    Configuration Loaded
                  </span>
                </div>

                <div className="card-body p-3 p-md-4">
                  <div className="row g-3">
                    <div className="col-12 col-md-6 col-xl-4">
                      <div className="order-config-item h-100">
                        <div className="order-config-item-icon">
                          <i className="ti ti-building-community"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="order-config-label">Organization</div>
                          <div className="order-config-value">
                            {orderFilter?.organization || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-4">
                      <div className="order-config-item h-100">
                        <div className="order-config-item-icon">
                          <i className="ti ti-briefcase-2"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="order-config-label">Account</div>
                          <div className="order-config-value">
                            {orderFilter?.project || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-4">
                      <div className="order-config-item h-100">
                        <div className="order-config-item-icon">
                          <i className="ti ti-map-pin"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="order-config-label">Location</div>
                          <div className="order-config-value">
                            {orderFilter?.location || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-4">
                      <div className="order-config-item h-100">
                        <div className="order-config-item-icon">
                          <i className="ti ti-folders"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="order-config-label">Program</div>
                          <div className="order-config-value">
                            {orderFilter?.program || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-4">
                      <div className="order-config-item h-100">
                        <div className="order-config-item-icon">
                          <i className="ti ti-building"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="order-config-label">Department</div>
                          <div className="order-config-value">
                            {orderFilter?.department || "-"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 col-xl-4">
                      <div className="order-config-item h-100">
                        <div className="order-config-item-icon">
                          <i className="ti ti-package"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="order-config-label">Package</div>
                          <div className="order-config-value">
                            {orderFilter?.package || "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="fw-semibold text-dark mb-3">
                Step 1: Order Configuration
              </div>
              <CommonFilterSelect
                colClass="col-6 col-sm-4 col-md-3 col-xl-2"
                handleGetFilters={(data) => {
                  setFilterData(data);
                }}
              />
            </>
          )}
        </div>

        {/* Invalidation reason */}
        <div className="alert alert-warning mb-4" role="alert">
          <h6 className="alert-heading mb-2">
            <i className="ti ti-alert-triangle me-2" />
            Reason for Invalidation
          </h6>

          <p className="mb-2">{invalidReason}</p>

          {duplicateField && (
            <p className="mb-1">
              <strong>Duplicate Field:</strong> {duplicateField}
            </p>
          )}

          {duplicateValue && (
            <p className="mb-0">
              <strong>Duplicate Value:</strong> {duplicateValue}
            </p>
          )}
        </div>

        {/* Step 2: Applicant details */}
        <div className="fw-semibold text-dark mb-3">
          Step 2 — Applicant Details
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Applicant Details</h5>
            </div>

            <div className="card-body row gx-2">
              <div className="col-12">
                <h6 className="mb-3">Personal Details</h6>
              </div>

              {renderField("First Name", "first_name", "text", true)}
              {renderField("Middle Name", "middle_name")}
              {renderField("Last Name", "last_name", "text", true)}
              {renderField("Date of Birth", "dob", "date", true)}

              <div className="col-6 col-md-4 mb-3">
                <label
                  htmlFor="review-gender"
                  className="form-label fw-bold text-dark"
                >
                  Gender <span className="text-danger">*</span>
                </label>

                <select
                  id="review-gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  disabled={loadingCreate}
                  className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>

                {errors.gender && (
                  <div className="invalid-feedback">{errors.gender}</div>
                )}
              </div>

              {renderField("Father Name", "parent_name", "text", true)}

              <div className="col-12 mt-3">
                <h6 className="mb-3">Contact Details</h6>
              </div>

              {renderField(
                "Applicant Email ID",
                "applicant_email",
                "email",
                true,
              )}
              {renderField(
                "Applicant Mobile Number",
                "applicant_mobile",
                "tel",
                true,
                10,
              )}

              <div className="col-12 mt-3">
                <h6 className="mb-3">Identity Details</h6>
              </div>

              {renderField("UAN No.", "uan_no", "text", false, 12)}
              {renderField("PAN No.", "pan_no", "text", true, 10)}
              {renderField(
                "Aadhaar Card Number",
                "aadharNumber",
                "text",
                true,
                12,
              )}

              <div className="col-12 mt-3">
                <h6 className="mb-3">Address Details</h6>
              </div>

              {renderField("Aadhaar Address", "address1", "text", true)}
              {renderField(
                "Aadhaar Address PIN Code",
                "aadhar_pincode",
                "text",
                true,
                6,
              )}
              {renderField("Applicant Address", "address2")}
              {renderField(
                "Applicant Address PIN Code",
                "address_pincode",
                "text",
                false,
                6,
              )}

              <div className="col-12 mt-3">
                <h6 className="mb-3">Employment Details</h6>
              </div>

              {renderField("Registration Number (CRN)", "crn_no")}
              {renderField("Candidate ID", "hrmsId", "text", true)}
              {renderField("Worker ID", "employeeId")}
              {renderField("Hire Date", "joiningDate", "date", true)}
              {renderField("SPOC Name", "spocName", "text", true)}
            </div>

            <div className="card-footer d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
                disabled={loadingCreate}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loadingCreate}
              >
                {loadingCreate ? "Saving..." : "Validate Candidate"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewCandidate;

// import React, { useEffect, useState } from "react";
// import "./ReviewCandidateModal.css";

// interface ReviewCandidateModalProps {
//   candidate: any;
//   onClose: () => void;
//   onConfirm: (updatedCandidate: any) => void;
// }

// const initialState = {
//   first_name: "",
//   middle_name: "",
//   last_name: "",
//   applicant_email: "",
//   applicant_mobile: "",
//   dob: "",
//   gender: "",
//   parent_name: "",
//   uan_no: "",
//   aadharNumber: "",
//   aadhar_pincode: "",
//   address_pincode: "",
//   pan_no: "",
//   address1: "",
//   address2: "",
//   crn_no: "",
//   hrmsId: "",
//   employeeId: "",
//   joiningDate: "",
//   spocName: "",
//   project_id: "",
// };

// const ReviewCandidateModal: React.FC<ReviewCandidateModalProps> = ({
//   candidate,
//   onClose,
//   onConfirm,
// }) => {
//   const [formData, setFormData] = useState(initialState);
//   const [errors, setErrors] = useState<Record<string, string>>({});
//   const [verified, setVerified] = useState(false);

//   useEffect(() => {
//     if (!candidate) return;

//     const address = candidate.metadata?.address ?? {};

//     setFormData({
//       first_name: candidate.first_name ?? "",
//       middle_name: candidate.middle_name ?? "",
//       last_name: candidate.last_name ?? "",
//       applicant_email: candidate.applicant_email ?? candidate.email ?? "",
//       applicant_mobile: String(
//         candidate.applicant_mobile ?? candidate.phone ?? "",
//       ),
//       dob: candidate.dob ? String(candidate.dob).slice(0, 10) : "",
//       gender: candidate.gender ?? "",
//       parent_name:
//         candidate.parent_name ?? candidate.father_or_mother_name ?? "",
//       uan_no: candidate.uan_no ?? "",
//       aadharNumber: candidate.aadharNumber ?? "",
//       aadhar_pincode: String(candidate.aadhar_pincode ?? ""),
//       address_pincode: String(
//         candidate.address_pincode ?? address.pinCode ?? "",
//       ),
//       pan_no: candidate.pan_no ?? "",
//       address1: candidate.address1 ?? address.addressLine1 ?? "",
//       address2: candidate.address2 ?? address.addressLine2 ?? "",
//       crn_no: candidate.crn_no ?? candidate.crn ?? "",
//       hrmsId: candidate.hrmsId ?? "",
//       employeeId: candidate.employeeId ?? candidate.employee_id ?? "",
//       joiningDate: candidate.joiningDate
//         ? String(candidate.joiningDate).slice(0, 10)
//         : candidate.joining_date
//           ? String(candidate.joining_date).slice(0, 10)
//           : "",
//       spocName: candidate.spocName ?? "",
//       project_id: candidate.project_id ?? "",
//     });

//     setErrors({});
//     setVerified(false);
//   }, [candidate]);

//   if (!candidate) return null;

//   const invalidReason =
//     candidate.invalid_reason ??
//     candidate.validation_reason ??
//     candidate.reason ??
//     "Invalid reason is not available.";

//   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const { name, value } = e.target;

//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));

//     setErrors((prev) => ({
//       ...prev,
//       [name]: "",
//     }));
//   };

//   const validateForm = () => {
//     const nextErrors: Record<string, string> = {};

//     if (!formData.first_name.trim() && !formData.last_name.trim()) {
//       nextErrors.first_name = "Candidate name is required.";
//     }

//     if (!/^[6-9]\d{9}$/.test(formData.applicant_mobile.trim())) {
//       nextErrors.applicant_mobile = "Enter a valid 10-digit mobile number.";
//     }

//     if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.applicant_email.trim())) {
//       nextErrors.applicant_email = "Enter a valid email address.";
//     }

//     if (!formData.crn_no.trim()) {
//       nextErrors.crn_no = "Registration number / CRN is required.";
//     }

//     if (!verified) {
//       nextErrors.verified = "Please confirm that you verified the details.";
//     }

//     setErrors(nextErrors);
//     return Object.keys(nextErrors).length === 0;
//   };

//   const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
//     e.preventDefault();

//     if (!validateForm()) return;

//     onConfirm({
//       ...candidate,
//       ...formData,
//       // Preserve the original API field names as well.
//       email: formData.applicant_email,
//       phone: formData.applicant_mobile,
//       crn: formData.crn_no,
//       employee_id: formData.employeeId,
//       joining_date: formData.joiningDate,
//       father_or_mother_name: formData.parent_name,
//       metadata: {
//         ...candidate.metadata,
//         address: {
//           ...candidate.metadata?.address,
//           addressLine1: formData.address1,
//           addressLine2: formData.address2,
//           pinCode: formData.address_pincode,
//         },
//       },
//     });
//   };

//   const renderField = (
//     label: string,
//     name: keyof typeof initialState,
//     type = "text",
//     required = false,
//   ) => (
//     <div className="rcm-field" key={name}>
//       <label htmlFor={`rcm-${name}`}>
//         {label} {required && <span>*</span>}
//       </label>

//       <input
//         id={`rcm-${name}`}
//         name={name}
//         type={type}
//         value={formData[name]}
//         onChange={handleChange}
//         className={errors[name] ? "rcm-input-error" : ""}
//         placeholder={`Enter ${label.toLowerCase()}`}
//         maxLength={name === "applicant_mobile" ? 10 : undefined}
//         inputMode={
//           name === "applicant_mobile" ||
//           name === "aadharNumber" ||
//           name === "aadhar_pincode" ||
//           name === "address_pincode"
//             ? "numeric"
//             : undefined
//         }
//       />

//       {errors[name] && <small className="rcm-error">{errors[name]}</small>}
//     </div>
//   );

//   return (
//     <div
//       className="rcm-backdrop"
//       role="presentation"
//       onMouseDown={(e) => {
//         if (e.target === e.currentTarget) onClose();
//       }}
//     >
//       <div
//         className="rcm-modal"
//         role="dialog"
//         aria-modal="true"
//         aria-labelledby="rcm-title"
//       >
//         <form onSubmit={handleSubmit} noValidate>
//           {/* Modal Header */}
//           <div className="rcm-header">
//             <div className="rcm-heading">
//               <div className="rcm-heading-icon">
//                 <i className="ti ti-user-check" />
//               </div>

//               <div>
//                 <h5 id="rcm-title">Review Candidate</h5>
//                 <p>Review details and resolve validation issues.</p>
//               </div>
//             </div>

//             <button
//               type="button"
//               className="rcm-close"
//               onClick={onClose}
//               aria-label="Close modal"
//             >
//               <i className="ti ti-x" />
//             </button>
//           </div>

//           <div className="rcm-body">
//             {/* Candidate Summary */}
//             <div className="rcm-summary">
//               <div className="rcm-avatar">
//                 {(
//                   `${formData.first_name} ${formData.last_name}`
//                     .trim()
//                     .charAt(0) || "C"
//                 ).toUpperCase()}
//               </div>

//               <div className="rcm-summary-info">
//                 <h6>
//                   {`${formData.first_name} ${formData.middle_name} ${formData.last_name}`
//                     .replace(/\s+/g, " ")
//                     .trim() || "Unnamed Candidate"}
//                 </h6>

//                 <span>CRN: {formData.crn_no || "Not provided"}</span>
//               </div>

//               <span className="rcm-status">
//                 <span className="rcm-status-dot" />
//                 Invalid
//               </span>
//             </div>

//             {/* Invalid Reason */}
//             <div className="rcm-reason">
//               <div className="rcm-reason-icon">
//                 <i className="ti ti-alert-triangle" />
//               </div>

//               <div>
//                 <h6>Reason for invalidation</h6>
//                 <p>{String(invalidReason)}</p>
//               </div>
//             </div>

//             {/* Personal Details */}
//             <div className="rcm-section-heading">
//               <h6>Personal Details</h6>
//               <span>* Required fields</span>
//             </div>

//             <div className="rcm-form-grid">
//               {renderField("First Name", "first_name", "text", true)}
//               {renderField("Middle Name", "middle_name")}
//               {renderField("Last Name", "last_name", "text", true)}
//               {renderField("Date of Birth", "dob", "date")}
//               {renderField("Gender", "gender")}
//               {renderField("Father / Mother Name", "parent_name")}
//             </div>

//             {/* Contact Details */}
//             <div className="rcm-section-heading">
//               <h6>Contact Details</h6>
//             </div>

//             <div className="rcm-form-grid">
//               {renderField("Applicant Email", "applicant_email", "email", true)}
//               {renderField("Applicant Mobile", "applicant_mobile", "tel", true)}
//             </div>

//             {/* Identity Details */}
//             <div className="rcm-section-heading">
//               <h6>Identity Details</h6>
//             </div>

//             <div className="rcm-form-grid">
//               {renderField("UAN Number", "uan_no")}
//               {renderField("Aadhaar Number", "aadharNumber")}
//               {renderField("Aadhaar PIN Code", "aadhar_pincode")}
//               {renderField("PAN Number", "pan_no")}
//             </div>

//             {/* Address Details */}
//             <div className="rcm-section-heading">
//               <h6>Address Details</h6>
//             </div>

//             <div className="rcm-form-grid">
//               {renderField("Address Line 1", "address1")}
//               {renderField("Address Line 2", "address2")}
//               {renderField("Address PIN Code", "address_pincode")}
//             </div>

//             {/* Employment Details */}
//             <div className="rcm-section-heading">
//               <h6>Employment Details</h6>
//             </div>

//             <div className="rcm-form-grid">
//               {renderField("CRN / Registration Number", "crn_no", "text", true)}
//               {renderField("HRMS ID", "hrmsId")}
//               {renderField("Employee ID", "employeeId")}
//               {renderField("Joining Date", "joiningDate", "date")}
//             </div>

//             {/* Project Details */}
//             <div className="rcm-section-heading">
//               <h6>Project Details</h6>
//             </div>

//             <div className="rcm-form-grid">
//               {renderField("SPOC Name", "spocName")}
//               {renderField("Project ID", "project_id")}
//             </div>

//             {/* Confirmation */}
//             <div className="rcm-confirmation">
//               <label className="rcm-checkbox-label">
//                 <input
//                   type="checkbox"
//                   checked={verified}
//                   onChange={(e) => {
//                     setVerified(e.target.checked);
//                     setErrors((prev) => ({
//                       ...prev,
//                       verified: "",
//                     }));
//                   }}
//                 />

//                 <span>
//                   I have reviewed the details and confirmed that this candidate
//                   meets the required validation rules.
//                 </span>
//               </label>

//               {errors.verified && (
//                 <small className="rcm-error">{errors.verified}</small>
//               )}
//             </div>
//           </div>

//           {/* Footer Actions */}
//           <div className="rcm-footer">
//             <button
//               type="button"
//               className="rcm-btn rcm-btn-cancel"
//               onClick={onClose}
//             >
//               Cancel
//             </button>

//             <button type="submit" className="rcm-btn rcm-btn-primary">
//               <i className="ti ti-check" />
//               Validate Candidate
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default ReviewCandidateModal;
